import Papa from 'papaparse';
import { HOURS_PER_DAY } from 'src/enums/general';
import { simulationChartType } from 'src/enums/simulations';
import { unum } from '@oliasoft-open-source/units';

export const CHART_PRIMARY_COLOR = 'hsl(61, 33%, 60%)';
export const CHART_MEDIUM_NEUTRAL_COLOR = 'hsl(60, 10%, 35%)';

export const colors = [
  'hsl(60, 10%, 70%)', // Light neutral
  CHART_MEDIUM_NEUTRAL_COLOR,
  'hsl(60, 10%, 15%)', // Dark neutral
  CHART_PRIMARY_COLOR,
];

export const getLineColor = (index, linesNumber = 1) => {
  const startHue = 60;
  const saturation = 40;
  const lightness = 60;
  const count = linesNumber > 0 ? linesNumber : 1;

  const gap = 360 / count;

  const hue = startHue + index * gap;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const moveInArray = (arr, from, to) => {
  const item = arr.splice(from, 1);
  arr.splice(to, 0, item[0]);
};

export const generateLabel = (el) => (el === 'mean' ? `P(${el})` : `P${el}`);

export const generateHorizontalBartChartViewData = (data, selectedDataEntity) => {
  const dataKeys = Object.keys(data);
  const result = dataKeys.map((el, i) => {
    return {
      label: generateLabel(el),
      data: data[el].map((item) => (selectedDataEntity === 0 ? item / 24 : item)),
      borderColor: getLineColor(i, dataKeys.length),
      backgroundColor: getLineColor(i, dataKeys.length),
    };
  });

  moveInArray(result, 2, 1);
  return result;
};

export const getScatterChartPoints = (data, selectedDataEntity, depthUnit) => {
  const result = [];
  const length = selectedDataEntity === 0 ? data?.time?.length : data?.costs?.length;
  for (let i = 0; i < length; i += 1) {
    result.push({
      x: selectedDataEntity === 0 ? data?.time[i] / 24 : data?.costs[i],
      y: unum(data.depth[i], depthUnit, 'm'),
      label: '',
    });
  }

  return result;
};

export const generateScatterChartViewData = (data, selectedDataEntity, depthUnit, scatterChartOptions) => {
  if (data) {
    const getScatterChartElement = (element, i, linesNumber) => {
      const { showPoints, backgroundColor, borderColor } = scatterChartOptions || {
        showPoints: true,
        backgroundColor: '',
        borderColor: '',
      };
      const color = getLineColor(i, linesNumber);
      if (showPoints) {
        return {
          label: generateLabel(element),
          data: getScatterChartPoints(data[element], selectedDataEntity, depthUnit),
          borderColor: color,
          pointBackgroundColor: color,
        };
      }

      return {
        label: generateLabel(element),
        data: getScatterChartPoints(data[element], selectedDataEntity, depthUnit),
        pointBackgroundColor: color,
        fill: element === '10' ? '1' : false,
        showPoints: false,
        backgroundColor,
        borderColor: element === '50' ? borderColor : 'transparent',
        hideLegend: true,
      };
    };

    const dataKeys = Object.keys(data);
    const result = dataKeys.map((el, i) => getScatterChartElement(el, i, dataKeys.length));

    moveInArray(result, 2, 1);
    return result;
  } else return [];
};

export const convertOperationDurations = (durations = {}) =>
  Object.keys(durations).reduce(
    (acc, el) => ({
      ...acc,
      [el]: durations[el]?.map((d) => d / HOURS_PER_DAY),
    }),
    {},
  );

export const convertOperationVsDepthDurations = (operationVsDepth = {}) => {
  const updadtedOperationVsDepth = {};
  for (const key in operationVsDepth) {
    if (operationVsDepth) {
      updadtedOperationVsDepth[key] = {
        ...operationVsDepth[key],
        time: operationVsDepth[key].time?.map((t) => t / HOURS_PER_DAY),
      };
    }
  }
  return updadtedOperationVsDepth;
};

const hasChildrenInFirstColmun = [
  simulationChartType.SECTION_DURATIONS,
  simulationChartType.OPERATION_DURATIONS,
  simulationChartType.TIME_DEPTH,
];

export const getRowsData = (rows, title) => {
  return rows.map((row, rowIdx) => {
    return row.cells?.map((cell, idx) => {
      if (
        title === simulationChartType.COST_ITEM_OVERVIEW &&
        idx === 1 &&
        rowIdx !== rows.length - 1 // last cell total cost is not a react component
      ) {
        return cell.value.props.children[2].props.children;
      } else if (hasChildrenInFirstColmun.includes(title) && idx === 0) {
        return cell.value.props.children;
      }
      return cell?.value ?? '';
    });
  });
};
export const unParse = (headers, rows, config) => {
  const { delimiter } = config;
  const fields = headers[0].cells.map((cell) => cell.value);
  const units = headers[1]?.cells.map((cell) => cell.value?.props?.children || '') ?? '';
  const input = [fields, units, ...rows];
  return Papa.unparse(input, {
    quotes: false,
    header: true,
    delimiter,
  });
};

export const generateVerticalStackedBarChartViewData = (chartData, labels) => {
  const linesCount = labels.length;
  return labels.map((label, index) => ({
    label,
    data: Object.values(chartData).map((data) => data[index]),
    borderColor: getLineColor(index, linesCount),
    backgroundColor: getLineColor(index, linesCount),
  }));
};
export const shownCumulativeProbabilities = [0, 9, 19, 29, 39, 49, 59, 69, 79, 89, 98];
export const shownCumulativeProbabilitiesForChart = [0, 9, 19, 29, 39, 49, 59, 69, 79, 89, 98];

export const generateCumulativeChartData = (dataForChart = [], shownItems = []) => {
  if (shownItems.length > 0) {
    return dataForChart.reduce((res, element, i) => {
      if (shownItems.includes(i)) {
        res.push({ x: element, y: i + 1 });
      }

      return res;
    }, []);
  } else {
    return dataForChart.map((el, idx) => ({ x: el, y: idx + 1 }));
  }
};
